import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { formatCurrency, formatFullCurrency } from './components';

/** Section heading with a plain-language question as the subtitle. */
export function SectionHeader(props) {
    const { title, question, tokens } = props;
    return (
        <Box sx={{ mt: 4, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
            {question && (
                <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                    {question}
                </Typography>
            )}
            <Divider sx={{ mt: 1 }} />
        </Box>
    );
}

/**
 * The money waterfall: what customers were billed, down to what is actually kept.
 *
 * Rendered as labelled rows rather than a chart on purpose - each step is a
 * subtraction the reader needs to follow, and a stacked bar would make the
 * arithmetic harder to check, not easier.
 */
export function MoneyWaterfall(props) {
    const { health, tokens } = props;

    const steps = [
        { label: 'Gross sales', hint: 'billed to customers before discounts',
          value: health.gross_sales, kind: 'total' },
        { label: 'Restaurant discounts', hint: 'promos and offers you funded',
          value: -health.restaurant_discount, kind: 'deduction' },
        { label: 'Net order value', hint: 'what we computed as revenue',
          value: health.net_order_value, kind: 'subtotal' },
        { label: 'Platform commission and fees', hint: 'commission, service fees, withheld tax',
          value: health.platform_take === null ? null : -health.platform_take,
          kind: 'deduction' },
        { label: 'Money actually received', hint: health.is_estimate
            ? `estimated from a ${health.observed_take_rate_pct}% take rate`
            : 'from payout statements',
          value: health.estimated_payout, kind: 'subtotal' },
        { label: 'Ingredient purchases',
          hint: health.cost_period_narrowed
              ? `bills dated ${health.cost_period_start} to ${health.cost_period_end}`
              : 'bills dated in this period',
          value: health.purchase_cost === null ? null : -health.purchase_cost,
          kind: 'deduction' },
        { label: 'Estimated net profit', hint: 'before rent, salaries and utilities',
          value: health.estimated_net_profit, kind: 'result' },
    ];

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
            {steps.map((step) => {
                const unknown = step.value === null || step.value === undefined;
                const isResult = step.kind === 'result';
                const negative = !unknown && step.value < 0;
                const color = step.kind === 'deduction' ? tokens.critical
                    : isResult ? (step.value < 0 ? tokens.critical : tokens.good)
                        : tokens.textPrimary;
                return (
                    <Box key={step.label}>
                        {isResult && <Divider sx={{ my: 1 }} />}
                        <Box sx={{
                            display: 'flex', alignItems: 'baseline', gap: 2,
                            justifyContent: 'space-between',
                            py: isResult ? 1 : 0.6,
                        }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant={isResult ? 'subtitle1' : 'body2'}
                                    sx={{ fontWeight: isResult ? 600 : 400 }}
                                >
                                    {step.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                    {step.hint}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: isResult ? 700 : 500,
                                    fontSize: isResult ? '1.5rem' : '1rem',
                                    color: unknown ? tokens.textMuted : color,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {unknown ? '--'
                                    : `${negative ? '- ' : ''}${formatCurrency(Math.abs(step.value))}`}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}

            {health.is_estimate && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                    Payout statements cover {health.payout_coverage_pct}% of delivered
                    orders ({health.matched_orders} of {health.delivered_orders}), so the
                    lower half of this is an estimate. On the {health.matched_orders}{' '}
                    orders with both sides present,{' '}
                    {formatFullCurrency(health.matched_revenue)} of order value became{' '}
                    {formatFullCurrency(health.matched_payout)}.
                </Alert>
            )}
            {health.cost_period_narrowed && health.has_cost_data && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                    Purchases are counted only over {health.cost_period_start} to{' '}
                    {health.cost_period_end} - the days order reports actually cover -
                    so cost and sales are measured over the same period. Upload order
                    reports for the full range to widen this.
                </Alert>
            )}
            {!health.has_cost_data && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                    No purchase bills in this period, so profit cannot be calculated.
                </Alert>
            )}
        </Paper>
    );
}

const SEVERITY_META = {
    critical: { label: 'Needs attention', color: 'error' },
    warning: { label: 'Worth checking', color: 'warning' },
    info: { label: 'Observation', color: 'info' },
};

/**
 * Computed findings.
 *
 * Each carries the number that produced it, so a claim can be checked rather
 * than taken on trust. Nothing appears unless its threshold was crossed.
 */
export function FindingsList(props) {
    const { actions, tokens } = props;
    const findings = actions?.findings || [];

    if (findings.length === 0) {
        return (
            <Alert severity="success">
                Nothing crossed a reporting threshold for this period.
            </Alert>
        );
    }

    return (
        <Grid container spacing={2}>
            {findings.map((finding, index) => {
                const meta = SEVERITY_META[finding.severity] || SEVERITY_META.info;
                return (
                    <Grid size={{ xs: 12, md: 6 }} key={`${finding.title}-${index}`}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                <Chip size="small" label={meta.label} color={meta.color}
                                    variant="outlined" />
                                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                    {finding.evidence}
                                </Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {finding.title}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, color: tokens.textSecondary }}>
                                {finding.detail}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 500 }}>
                                {finding.action}
                            </Typography>
                        </Paper>
                    </Grid>
                );
            })}
        </Grid>
    );
}
